<script lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByTypes } from '@@/enums'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Id, Post, Section } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import SurfacePostsGridSectionDragPreview from '@@/vuecomponents/SurfacePostsGridSectionDragPreview.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { computed, ref } from 'vue'
import SurfacePostDraggable from '@@/vuecomponents/SurfacePostDraggable.vue'
import { storeToRefs } from 'pinia'

export const SURFACE_POSTS_GRID_SECTION_HEADER = 'surface_posts_grid_section'
export const SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE = 'surface_posts_grid_post'
export default {}
</script>

<script setup lang="ts">
const SCROLLBAR_WIDTH = 16
const NEXT_COLUMN_PEEK = 8
const HORIZONTAL_PADDING = 24

const props = withDefaults(
  defineProps<{
    group: Group
    postGroupHeaderHeights: Record<Group['id'], number>
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
    maxPostGroupHeaderHeight: number
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const emit = defineEmits<{
  (e: 'update-post-group-header-heights', groupId: Group['id'], height: number): void
  (e: 'remove-post-group-header-height', groupId: Group['id']): void
}>()

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()

const { backgroundLuminance, isPostSizeStandard, isPostSizeWide } = storeToRefs(useSurfaceStore())
const {
  isAPostDragging,
  groupIdUnderPostBeingDragged,
  shouldDisableDraggingPost,
  isDraggingPostInNonManuallySortedWall,
} = storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy, isGroupByNone } = storeToRefs(useSurfaceWishArrangementStore())

const { shouldDisplayPost } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore
const { windowHeight, windowWidth, isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())

const overlayWidth = computed(() => {
  // width on shelf/grid with sections is either 256 or 380
  const postElementWidth = isPostSizeStandard.value
    ? document.querySelector('.wish-v2')?.getBoundingClientRect().width ?? NON_STREAM_STANDARD_MAX_POST_SIZE
    : document.querySelector('.wish-v2')?.getBoundingClientRect().width ?? NON_STREAM_WIDE_MAX_POST_SIZE
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 12 : 0
})

const overlayTopPosition = computed(() => {
  const defaultTopMargin = 0
  if (groupIdUnderPostBeingDragged.value === undefined) return defaultTopMargin
  const hoveredSectionHeaderHeight = props.postGroupHeaderHeights[groupIdUnderPostBeingDragged.value]
  if (!hoveredSectionHeaderHeight) return defaultTopMargin

  if (hoveredSectionHeaderHeight === props.maxPostGroupHeaderHeight) return defaultTopMargin

  return props.maxPostGroupHeaderHeight - hoveredSectionHeaderHeight
})

const sectionWidth = computed(() => {
  if (!isSmallerThanTabletPortrait.value) {
    return isPostSizeStandard.value ? 272 : 396 // (256px/380px + 16px scrollbar)
  }

  // On mobile, calculate width to ensure even padding, but enforce minimum, and show 8px of next column
  const availableWidth = windowWidth.value - HORIZONTAL_PADDING - NEXT_COLUMN_PEEK
  const maxPostWidth = isPostSizeStandard.value ? NON_STREAM_STANDARD_MAX_POST_SIZE : NON_STREAM_WIDE_MAX_POST_SIZE
  const idealWidth = Math.min(availableWidth, maxPostWidth + SCROLLBAR_WIDTH)
  return Math.max(idealWidth, NON_STREAM_STANDARD_MAX_POST_SIZE) // Ensure minimum but show next column peek
})

if (!isAppUsing('pdndSurfaceGrid')) {
  useDragSortableInGroups({
    onDragStart: ({ groupId }) => {
      isAPostDragging.value = true
      groupIdUnderPostBeingDragged.value = groupId
    },
    onDragEnd: ({ postCid, postId, groupId }) => {
      isAPostDragging.value = false
      if (postCid && postId) {
        const sectionId =
          typeof groupId === 'number' && wishGroupBy.value === WallWishGroupByTypes.Section ? groupId : undefined
        const prevWish = document
          .querySelector(`#wish-${postId}`)
          ?.closest('.wish-wrapper')
          ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
        const nextWish = document
          .querySelector(`#wish-${postId}`)
          ?.closest('.wish-wrapper')
          ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
        const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
          sectionId,
          prevId: prevWish?.id,
          nextId: nextWish?.id,
        })
        surfacePostsStore.sortPost({
          postCid,
          sortIndex: sortIndex ?? undefined,
          sectionId,
          gapSize,
        })
      }
      groupIdUnderPostBeingDragged.value = undefined
    },
    format: 'grid',
  })
}

function updateMaxGroupHeaderHeight(groupId: Group['id'], { height }: DOMRectReadOnly): void {
  emit('update-post-group-header-heights', groupId, height)
}

/**
 * Make sure the group won't be scrolled horizontally on Safari
 * @param e
 */
function handleGroupScroll(e: Event) {
  if (!device.safari) return
  if (!isAPostDragging.value) return
  const target = e.target as HTMLElement
  if (target.scrollLeft > 0) {
    target.scrollLeft = 0
    e.preventDefault()
  }
}

// We use this to set a minHeight for the section container to avoid not being able to scroll down to the posts
// when a section has a long title that takes up all the vertical space (this happens on phone landscape)
const MIN_POSTS_CONTAINER_HEIGHT = 200
const maxPostGroupHeaderHeightPlusSome = computed(() => props.maxPostGroupHeaderHeight + MIN_POSTS_CONTAINER_HEIGHT)
const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  return isPostSizeStandard.value ? NON_STREAM_STANDARD_MAX_POST_SIZE : NON_STREAM_WIDE_MAX_POST_SIZE
})

// #region Drag and drop for section header
const { isASectionDragging } = storeToRefs(useSurfaceSectionsStore())
const groupHeaderSectionRef = ref<
  InstanceType<typeof SurfacePostGroupHeaderSection> | InstanceType<typeof SurfacePostGroupHeaderSectionOld>
>()
const sectionTitleRef = computed<HTMLElement | undefined>(() => {
  if (groupHeaderSectionRef.value === undefined) {
    return undefined
  }
  return groupHeaderSectionRef.value?.sectionTitleRef?.$el as HTMLElement
})
const groupIdBeingDragged = ref<string | number | undefined>(undefined)
const isCurrentSectionBeingDragged = computed(() => groupIdBeingDragged.value === props.group.id)
useDraggableElement({
  elementRef: sectionTitleRef,
  type: SURFACE_POSTS_GRID_SECTION_HEADER,
  data: computed(() => {
    return { section: props.group as Section }
  }),
  dragPreview: device.ios ? undefined : SurfacePostsGridSectionDragPreview,
  dragPreviewComponentProps: computed(() => ({
    section: props.group,
    width: Math.max(sectionWidth.value - SCROLLBAR_WIDTH, NON_STREAM_STANDARD_MAX_POST_SIZE),
  })),
  onDrag: () => {
    isASectionDragging.value = true
    groupIdBeingDragged.value = props.group.id
  },
  onDrop: () => {
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
  },
  isDraggable: computed(() => canIDragSections.value && isAppUsing('pdndSurfaceGrid')),
})
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef: sectionTitleRef,
  type: SURFACE_POSTS_GRID_SECTION_HEADER,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSource({
  type: SURFACE_POSTS_GRID_SECTION_HEADER,
  axis: 'horizontal',
  onDrop: (e: OnDropPayload<{ section: Section }, { section: Section }>) => {
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
    const sourceSection = e.sourceItem.data.section
    const targetSection = e.targetItem.data.section
    const { sortIndex, gapSize } = useSectionSorterHelper().getSortIndexAndGapSize(
      targetSection.id,
      e.relativePositionToTarget,
    )
    if (sortIndex === undefined || gapSize === undefined) return
    useSurfaceSectionsStore().sortSection({
      sectionId: sourceSection.id,
      sortIndex,
      gapSize,
    })
  },
  canDrop: () => {
    return canIDragSections.value && isAppUsing('pdndSurfaceGrid')
  },
})
// #endregion

// #region Drag and drop for posts into section
const sectionElementRef = ref<HTMLElement | undefined>(undefined)
// The edge should be bottom if the wall is non-manually sorted, and undefined if the wall is manually sorted
const closestEdgeToPointerForSection = ref<LogicalEdge | undefined>(undefined)
const { addDraggableSource: addDraggableSourceForSection } = useDropTargetElement({
  elementRef: sectionElementRef,
  type: SURFACE_POSTS_GRID_SECTION_HEADER,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSourceForSection({
  type: SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDrop: (e: OnDropPayload<{ post: Post }, { section: Section }>) => {
    isAPostDragging.value = false
    groupIdUnderPostBeingDragged.value = undefined
    closestEdgeToPointerForSection.value = undefined
    const sourcePost = e.sourceItem.data.post
    const targetGroupId = e.targetItem.data.section.id
    surfacePostsStore.updatePostSection({
      postCid: sourcePost.cid,
      sectionId: targetGroupId,
    })
  },
  onDragEnter: () => {
    groupIdUnderPostBeingDragged.value = props.group.id
    if (!isDraggingPostInNonManuallySortedWall.value) {
      closestEdgeToPointerForSection.value = 'bottom'
    }
  },
  onDragLeave: () => {
    closestEdgeToPointerForSection.value = undefined
  },
  canDrop: () => {
    if (!isAppUsing('pdndSurfaceGrid')) return false
    // If the wall is non-manually sorted, we allow dropping posts into the section
    if (isDraggingPostInNonManuallySortedWall.value) return true
    const posts = allSortedPostsByGroup.value[props.group.id]
    if (posts && posts.length > 0) return false
    // If the wall is manually sorted, we allow dropping posts into the section if the section is empty
    return true
  },
})
// #endregion
</script>

<template>
  <section
    :id="'section-' + group.id"
    ref="sectionElementRef"
    :key="group.id"
    :data-id="group.id"
    :data-rank="group.sort_index"
    :class="[
      'relative',
      'flex-none',
      'flex',
      'flex-col',
      // This list of sections should be centered on the screen.
      // Normally, we can do it with `justify-center` inside a
      // flex container. But since the content can overflow
      // horizontally, we can't use it but instead need to
      // set `margin-left: auto` and `margin-right: auto` on the
      // first and last elements respectively.
      // See: https://stackoverflow.com/a/24678299/8943850
      'first:!ms-auto',
      'last:!me-auto',
      {
        'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
      },
    ]"
    :style="{ width: `${sectionWidth}px`, minHeight: `${maxPostGroupHeaderHeightPlusSome}px` }"
  >
    <div
      v-if="canShowDraggingOverlayOnGroup(group.id)"
      :class="[
        'absolute -ms-2 z-underneath',
        'border-solid border-2 rounded-xl shadow-elevation-2',
        'pointer-events-none',
        {
          'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
          'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
        },
      ]"
      :style="{
        height: `calc(100% + 8px)`,
        width: `${overlayWidth}px`,
        top: `${overlayTopPosition}px`,
      }"
    />

    <!-- GROUP HEADER -->
    <!-- It should be the same size as the content area of the posts, but without the scrollbar -->
    <div
      v-if="wishGroupBy !== WallWishGroupByTypes.None"
      class="flex-none flex flex-col justify-end mb-3 tablet-portrait:mb-4 mt-2"
      :style="{
        height: `${maxPostGroupHeaderHeight}px`,
        width: `${Math.max(sectionWidth - SCROLLBAR_WIDTH, NON_STREAM_STANDARD_MAX_POST_SIZE)}px`,
      }"
    >
      <SurfacePostGroupHeaderSection
        v-if="isAppUsing('groupPostsV2') && !isGroupByNone"
        ref="groupHeaderSectionRef"
        :class="[isCurrentSectionBeingDragged && 'opacity-50']"
        :section="group"
        @resize="updateMaxGroupHeaderHeight(group.id, $event)"
        @before-unmount="emit('remove-post-group-header-height', group.id)"
      />
      <SurfacePostGroupHeaderSectionOld
        v-else-if="wishGroupBy === WallWishGroupByTypes.Section"
        ref="groupHeaderSectionRef"
        :class="[isCurrentSectionBeingDragged && 'opacity-50']"
        :section="group"
        @resize="updateMaxGroupHeaderHeight(group.id, $event)"
        @before-unmount="emit('remove-post-group-header-height', group.id)"
      />
    </div>

    <div v-if="closestEdgeToPointerForSection" class="relative">
      <OzDropIndicatorLine :edge="closestEdgeToPointerForSection" :gap="16" class="my-1 ms-1 me-4" />
    </div>

    <!-- POSTS IN GROUP -->
    <div
      :id="`group-posts-${group.id}`"
      :data-id="group.id"
      :class="[
        'grow',
        'flex',
        'flex-col',
        'space-y-2',
        'overflow-y-auto',
        'overflow-x-hidden',
        'group-posts',
        '-ms-1.5 ps-1.5',
        {
          'scrollbar-photo': backgroundLuminance === 'light',
          'scrollbar-photo-dark': backgroundLuminance === 'dark',
        },
      ]"
      :style="{
        width: `${Math.max(sectionWidth, NON_STREAM_STANDARD_MAX_POST_SIZE + SCROLLBAR_WIDTH)}px`,
        scrollbarGutter: 'stable',
      }"
      @scroll="handleGroupScroll"
    >
      <template v-for="(post, index) in allSortedPostsByGroup[group.id]">
        <SurfaceLazy
          v-show="shouldDisplayPost(post.cid)"
          :key="post.cid"
          :data-cid="post.cid"
          :data-id="post.id"
          :min-height="minHeight"
          :min-width="minWidth"
          :render-immediately="!isAppUsing('lazyRenderPosts')"
          class="wish-wrapper h-fit last:!mb-8 tablet-portrait:last:mb-4 -ms-1.5 ps-1.5 -me-3 pe-3.5"
        >
          <SurfacePost
            v-if="!isAppUsing('pdndSurfaceGrid')"
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
              'wish-v2',
              isPostSizeStandard && 'max-w-64',
              isPostSizeWide && 'max-w-95',
            ]"
            :index="index"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
          />
          <SurfacePostDraggable
            v-else
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
              'wish-v2',
              isPostSizeStandard && 'max-w-64',
              isPostSizeWide && 'max-w-95',
              // When dragging a post, we need to add some extra margin to the first post to allow space for the drop indicator line
              isAPostDragging && index === 0 && 'mt-3',
            ]"
            :index="index"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
            :drag-element-type="SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE"
            :drop-element-type="SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE"
            axis="vertical"
            :gap="8"
            :should-compress="isAPostDragging"
            :is-draggable="!shouldDisableDraggingPost"
            :group-id="group.id"
          />
        </SurfaceLazy>
      </template>
    </div>
    <OzDropIndicatorLine v-if="closestEdgeToPointer" :edge="closestEdgeToPointer" :gap="16" class="my-1" />
  </section>
</template>
